// agora.js
'use strict';

import loadScriptTag from '../resource/loadScriptTag.js';
import env from '../resource/env.js';
import { player as playerDebug } from '../resource/debug.js';
import {
  LOCAL_PUBLISH_STATE,
  releaseLocalPublishing,
} from '../resource/agoraLocalPublishing.js';

const log = playerDebug.extend('log:agora');
const errorLog = playerDebug.extend('error:agora');

let agoraClient;
// Agora SDK
let loadAgoraScriptPromise;
let currentAgoraInstance = null;
let pendingLeavePromise = null;

export const loadAgoraScript = async () => {
  if (loadAgoraScriptPromise) {
    await loadAgoraScriptPromise;
    return;
  }

  const scriptTagId = `agora-rtc-${env.AGORA_RTC_VERSION}`;
  loadAgoraScriptPromise = loadScriptTag({
    id: scriptTagId,
    src: `https://download.agora.io/sdk/release/AgoraRTC_N-${env.AGORA_RTC_VERSION}.js`,
  });

  log('start load agora client');
  try {
    await loadAgoraScriptPromise;
  } catch (error) {
    // Let a later call retry: clear the cached rejection and remove the
    // errored script tag, which loadScriptTag would keep rejecting from.
    loadAgoraScriptPromise = null;
    document.getElementById(scriptTagId)?.remove();
    throw error;
  }
  log('load agora client done');

  if (window.AgoraRTC) {
    // AgoraRTC default enable debug log
    // 0: DEBUG. Output all API logs.
    // 1: INFO. Output logs of the INFO, WARNING and ERROR level.
    // 2: WARNING. Output logs of the WARNING and ERROR level.
    // 3: ERROR. Output logs of the ERROR level.
    // 4: NONE. Do not output any log.
    let debugLevel = 4;
    if (new URLSearchParams(window.location.search).has('debug-agora')) {
      debugLevel = +new URLSearchParams(window.location.search).get(
        'debug-agora'
      );
    }
    window.AgoraRTC.setLogLevel(debugLevel);
  }
};

// https://api-ref.agora.io/en/video-sdk/web/4.x/enums/audiencelatencyleveltype.html
// 1: Low latency. 2: (Default) Ultra low latency.
export const AGORA_AUDIENCE_LATENCY_LEVEL = 1;
// https://doc.shengwang.cn/api-ref/rtc/javascript/globals#clientrole
export const AGORA_CLIENT_ROLE = { AUDIENCE: 'audience', HOST: 'host' };

export const initializeAgoraClient = ({ codec }) => {
  log('execute initialize agora client', { codec });
  // create client reference: https://api-ref.agora.io/en/video-sdk/web/4.x/interfaces/clientconfig.html
  return window.AgoraRTC.createClient({
    mode: 'live',
    role: AGORA_CLIENT_ROLE.AUDIENCE,
    clientRoleOptions: {
      // reference: https://doc.shengwang.cn/doc/rtc/javascript/basic-features/ls-quick-start
      level: AGORA_AUDIENCE_LATENCY_LEVEL,
    },
    codec,
  });
};

// https://api-ref.agora.io/en/video-sdk/web/4.x/globals.html#connectionstate
export const AGORA_CLIENT_CONNECTION_STATE = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  RECONNECTING: 'RECONNECTING',
  DISCONNECTING: 'DISCONNECTING',
};

// https://doc.shengwang.cn/api-ref/rtc/javascript/enums/remotestreamtype
export const REMOTE_STREAM_TYPE = {
  HIGH: 0,
  LOW: 1,
};

// https://doc.shengwang.cn/api-ref/rtc/javascript/enums/remotestreamfallbacktype
export const REMOTE_STREAM_FALLBACK_TYPE = {
  DISABLE: 0,
  VIDEO_STREAM_LOW: 1,
  AUDIO_ONLY: 2,
};

export const getAgoraConnectionState = ({ channelId }) => {
  return getAgoraInstance({ channelId })?.client?.connectionState;
};

export const bindAgoraRTCEventListeners = ({ listeners }) => {
  listeners.forEach(({ eventName, listener }) => {
    window.AgoraRTC?.on(eventName, listener);
  });
};

export const unbindAgoraRTCEventListeners = ({ listeners }) => {
  listeners.forEach(({ eventName, listener }) => {
    window.AgoraRTC?.off(eventName, listener);
  });
};

export const cleanupAgoraRTCListeners = () => {
  window.AgoraRTC?.removeAllListeners();
};

// Agora Instance Operations (listeners bind/unbind & renew token & leave)
export const bindAgoraClientEventListeners = ({ channelId, listeners }) => {
  const agoraClient = getAgoraInstance({ channelId })?.client;
  listeners.forEach(({ eventName, listener }) => {
    agoraClient?.on(eventName, listener);
  });
};

export const unbindAgoraClientEventListeners = ({ channelId, listeners }) => {
  const agoraClient = getAgoraInstance({ channelId })?.client;
  listeners.forEach(({ eventName, listener }) => {
    agoraClient?.off(eventName, listener);
  });
};

export const renewToken = async ({ channelId, token }) => {
  const agoraClient = getAgoraInstance({ channelId })?.client;
  if (!agoraClient) {
    errorLog('fail to renew token: agora client not found', {
      channelId,
      token,
    });
    return;
  }
  try {
    await agoraClient.renewToken(token);
    log('successfully renewed token', { channelId, token });
  } catch (error) {
    errorLog('fail to renew token', { channelId, token, error });
    throw error;
  }
};

/**
 * Agora Client Promise Chain Flow
 *
 * joinChannelWithoutSubscribingPromise (Preloader 觸發，Player 複用)
 * │
 * ├─ 1. loadScriptPromise           ← loadAgoraScript()
 * │
 * ├─ 2. createClientPromise          ← initializeAgoraClient()
 * │     └─ bindInternalClientListeners()
 * │
 * ├─ 3. joinChannelPromise           ← client.join(appId, channelId, token, sessionId)
 * │
 * └─ 4. waitForPublishingPromise     ← wait video + audio user-published (10s timeout)
 *       └─ resolve()
 *
 *
 * joinChannelWithSubscribingPromise (Player 觸發)
 * │
 * ├─ reuse joinChannelWithoutSubscribingPromise
 * │  (若 Preloader 已建立 → 直接 await，不重建)
 * │
 * └─ 5. subscribeTrackPromise        ← client.subscribe(remoteUser, 'video')
 *                                       client.subscribe(remoteUser, 'audio')
 *       └─ return { client, track: { videoTrack, audioTrack } }
 *
 *
 * AgoraInstance Property Reset Levels
 * (僅重置 agoraInstance 上的 cached promise，不會觸發 SDK 的 join/leave/subscribe)
 *
 * resetSubscription ⊂ resetPublishing ⊂ resetConnection
 *
 * resetSubscription: subscribeTrackPromise, joinChannelWithSubscribingPromise
 * resetPublishing:   + waitForPublishingPromise, joinChannelWithoutSubscribingPromise, publishedResolve
 * resetConnection:   + joinChannelPromise, publishedTracks
 */

const WAIT_FOR_PUBLISHING_TIMEOUT_MS = 10000;
export const AGORA_ERROR_WAIT_FOR_PUBLISHING_TIMEOUT =
  'agora_player_error_9999_publishing_timeout';

// --- AgoraInstance Property Reset Helpers (no SDK operations) ---

const getIsCurrentAgoraInstance = agoraInstance => {
  return (
    currentAgoraInstance === agoraInstance &&
    !agoraInstance.abortController.signal.aborted
  );
};

// Abort async flows in agoraClient join / subscribe processes
const waitWithAbort = (promise, agoraInstance) => {
  const { signal } = agoraInstance.abortController;
  if (signal.aborted) return Promise.resolve(null);

  return new Promise((resolve, reject) => {
    const handleAbort = () => resolve(null);
    signal.addEventListener('abort', handleAbort, { once: true });
    Promise.resolve(promise).then(
      value => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      error => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

const abortAgoraInstance = agoraInstance => {
  if (!agoraInstance.abortController.signal.aborted) {
    agoraInstance.abortController.abort();
  }
};

const resetSubscription = agoraInstance => {
  agoraInstance.subscribeTrackPromise = null;
  agoraInstance.joinChannelWithSubscribingPromise = null;
};

const resetPublishing = agoraInstance => {
  resetSubscription(agoraInstance);
  // Settle a pending wait before dropping the resolver, or a joiner awaiting
  // waitForPublishingPromise would hang forever (its timeout is also cleared
  // below), leaving the player stuck without its recovery listeners bound.
  // Resolve rather than reject: the joiner proceeds with whatever tracks
  // remain and recovers via user-published, while a rejection would cascade
  // into the joiner's catch and trigger a full leaveAgoraChannel.
  agoraInstance.publishedResolve?.();
  agoraInstance.waitForPublishingPromise = null;
  agoraInstance.joinChannelWithoutSubscribingPromise = null;
  agoraInstance.publishedResolve = null;
  clearTimeout(agoraInstance.publishingTimeoutId);
  agoraInstance.publishingTimeoutId = null;
};

const resetConnection = agoraInstance => {
  resetPublishing(agoraInstance);
  agoraInstance.joinChannelPromise = null;
  agoraInstance.publishedTracks = {};
};

// isAudioLive is the stream's identity (a stream never switches between
// audio-live and regular mid-stream), so it is fixed here at creation and
// treated as read-only afterward — never mutate it per join.
const createAgoraEntry = (channelId, isAudioLive) => ({
  channelId,
  isAudioLive,
  sourceId: null,
  abortController: new AbortController(),
  client: null,
  loadScriptPromise: null,
  createClientPromise: null,
  joinChannelPromise: null,
  waitForPublishingPromise: null,
  subscribeTrackPromise: null,
  joinChannelWithoutSubscribingPromise: null,
  joinChannelWithSubscribingPromise: null,
  publishedResolve: null,
  publishingTimeoutId: null,
  publishedTracks: {},
  // References used only to unpublish viewer tracks from Agora.
  localPublishedTracks: null,
  // Viewer publish state; separate from the streamer's publishedTracks above.
  localPublishState: LOCAL_PUBLISH_STATE.IDLE,
  // Serializes the initial publish and later track reconciliation.
  localPublishPromise: null,
  // Used to report how long the viewer published.
  localPublishedAt: null,
  isNearBy: false,
});

/**
 * Check whether the required media tracks are all present. Audio-live streams
 * publish audio only (no video track), so they require audio only; regular
 * streams require video + audio.
 * @param {object} params
 * @param {*} params.hasVideo - truthy when the video requirement is met.
 * @param {*} params.hasAudio - truthy when the audio requirement is met.
 * @param {boolean} params.isAudioLive - whether the stream is audio-live.
 * @returns {boolean} true when all required tracks are present.
 * @example
 * hasRequiredTracks({ hasVideo: false, hasAudio: true, isAudioLive: true });
 * // => true
 * hasRequiredTracks({ hasVideo: false, hasAudio: true, isAudioLive: false });
 * // => false
 */
export const hasRequiredTracks = ({ hasVideo, hasAudio, isAudioLive }) =>
  isAudioLive ? !!hasAudio : !!(hasVideo && hasAudio);

const getHasRequiredPublishedTracks = agoraInstance =>
  hasRequiredTracks({
    hasVideo: agoraInstance.publishedTracks.video,
    hasAudio: agoraInstance.publishedTracks.audio,
    isAudioLive: agoraInstance.isAudioLive,
  });

export const getAgoraInstance = ({ channelId } = {}) => {
  if (!currentAgoraInstance) return null;
  if (channelId && currentAgoraInstance.channelId !== channelId) return null;
  return currentAgoraInstance;
};

export const acquireAgoraInstance = async ({
  channelId,
  isAudioLive = false,
  sourceId,
} = {}) => {
  if (!channelId) {
    throw new Error('acquireAgoraInstance: missing channelId');
  }

  if (pendingLeavePromise) {
    await pendingLeavePromise;
  }

  if (currentAgoraInstance?.channelId === channelId) {
    // Reusing an existing instance:
    // if sourceId arrives late, update it so later stream picking / event filtering can use it.
    if (sourceId != null) currentAgoraInstance.sourceId = sourceId;
    return currentAgoraInstance;
  }

  if (currentAgoraInstance) {
    await leaveAgoraChannel({ channelId: currentAgoraInstance.channelId });
  }

  currentAgoraInstance = createAgoraEntry(channelId, isAudioLive);
  if (sourceId != null) currentAgoraInstance.sourceId = sourceId;
  return currentAgoraInstance;
};

export const leaveAgoraChannel = async ({ channelId } = {}) => {
  const targetChannelId = channelId || currentAgoraInstance?.channelId;
  // Device ownership is independent of an Agora instance/client. Release it
  // even when the channel failed before an Agora entry could be acquired.
  if (targetChannelId) {
    releaseLocalPublishing({ channelId: targetChannelId });
  }

  // Assumes at most one instance exists at a time, so a pending leave is
  // always for the same channel; a second concurrent leave only waits for it
  // and does not leave a different channel.
  if (pendingLeavePromise) {
    await pendingLeavePromise;
    return;
  }

  const agoraInstance = getAgoraInstance({ channelId });
  if (!agoraInstance) return;

  pendingLeavePromise = (async () => {
    abortAgoraInstance(agoraInstance);
    resetConnection(agoraInstance);
    const client = agoraInstance.client || agoraClient;
    if (!client) return;

    client.removeAllListeners();
    await client.leave().catch(error => {
      errorLog('leaveAgoraChannel: fail to leave channel', {
        channelId: agoraInstance.channelId,
        error,
      });
    });
  })();

  try {
    await pendingLeavePromise;
  } finally {
    if (currentAgoraInstance === agoraInstance) {
      currentAgoraInstance = null;
    }
    pendingLeavePromise = null;
  }
};

/**
 * Whether a remote user is the streamer (uid === sourceId). While sourceId is
 * unknown, match no one (fail closed) instead of accepting everyone.
 * @param {object} params
 * @param {string} [params.sourceId] - streamer uid from redux; falsy = unknown.
 * @param {object} params.remoteUser - Agora remote user.
 * @returns {boolean} true if the remote user is the streamer.
 * @example
 * getIsStreamerRemoteUser({ sourceId: 'source_x', remoteUser }); // => false for the buyer
 */
export const getIsStreamerRemoteUser = ({ sourceId, remoteUser } = {}) =>
  !!sourceId && remoteUser?.uid === sourceId;

/**
 * Pick the streamer (uid === sourceId) from remote users. While sourceId is
 * unknown, return null (fail closed) instead of another publisher.
 */
export const getStreamerRemoteUser = ({ sourceId, remoteUsers = [] } = {}) =>
  sourceId
    ? remoteUsers.find(remoteUser => remoteUser?.uid === sourceId) ?? null
    : null;

// --- Internal Helpers ---

// Collect the streamer's (uid === sourceId) tracks only; with sourceId
// unknown or the streamer not publishing, return empty (never the buyer's).
const collectTracksFromRemoteUsers = (client, agoraInstance) => {
  const sourceId = agoraInstance?.sourceId;
  const remoteUsers = sourceId
    ? [getStreamerRemoteUser({ sourceId, remoteUsers: client?.remoteUsers })]
    : [];
  let uid = null;
  let videoTrack = null;
  let audioTrack = null;
  for (const remoteUser of remoteUsers) {
    if (!remoteUser) continue;
    if (remoteUser?.videoTrack) {
      uid = remoteUser.uid;
      videoTrack = remoteUser.videoTrack;
    }
    if (remoteUser?.audioTrack) {
      uid = remoteUser.uid;
      audioTrack = remoteUser.audioTrack;
    }
  }
  return { uid, videoTrack, audioTrack };
};

const bindInternalClientListeners = (client, agoraInstance, channelId) => {
  client.on('user-published', (remoteUser, mediaType) => {
    // Only accept the streamer's publish (uid === sourceId); while sourceId
    // is unknown, skip everyone so the buyer never enters publishedTracks.
    if (
      !getIsStreamerRemoteUser({ sourceId: agoraInstance.sourceId, remoteUser })
    ) {
      // Log the unknown-sourceId window so it stays observable.
      if (!agoraInstance.sourceId) {
        errorLog('user-published: skipped, sourceId unknown', {
          channelId,
          mediaType,
          uid: remoteUser?.uid,
          remoteUserCount: client.remoteUsers?.length ?? 0,
        });
      }
      return;
    }
    agoraInstance.publishedTracks[mediaType] = remoteUser;
    log('user-published', { channelId, mediaType, uid: remoteUser.uid });
    if (
      getHasRequiredPublishedTracks(agoraInstance) &&
      agoraInstance.publishedResolve
    ) {
      // Cancel the timeout race so it won't reject after resolve.
      clearTimeout(agoraInstance.publishingTimeoutId);
      agoraInstance.publishingTimeoutId = null;
      agoraInstance.publishedResolve();
    }
  });

  client.on('user-unpublished', (remoteUser, mediaType) => {
    // Only handle the streamer's (remote user uid = sourceId) unpublish; the buyer turning off their camera
    // must not trigger resetPublishing (it would corrupt the streamer stream state).
    if (
      !getIsStreamerRemoteUser({ sourceId: agoraInstance.sourceId, remoteUser })
    )
      return;
    log('user-unpublished', { channelId, mediaType });
    delete agoraInstance.publishedTracks[mediaType];
    resetPublishing(agoraInstance);
  });

  client.on('connection-state-change', (curState, prevState, reason) => {
    log('connection-state-change', { channelId, curState, prevState, reason });
    if (curState === 'DISCONNECTED') {
      resetConnection(agoraInstance);
    }
  });
};

// --- Remote Stream Selection & Fallback Helpers ---

// https://doc.shengwang.cn/api-ref/rtc/javascript/interfaces/iagorartcclient#setRemoteDefaultVideoStreamType
const setAgoraRemoteDefaultVideoStreamType = async ({ client, channelId }) => {
  const defaultStreamType = REMOTE_STREAM_TYPE.LOW;
  try {
    await client.setRemoteDefaultVideoStreamType(defaultStreamType);
    log('setRemoteDefaultVideoStreamType: success', {
      channelId,
      defaultStreamType,
    });
  } catch (error) {
    errorLog('setRemoteDefaultVideoStreamType: failed', {
      channelId,
      defaultStreamType,
      error,
    });
  }
};

// https://doc.shengwang.cn/api-ref/rtc/javascript/interfaces/iagorartcclient#setStreamFallbackOption
const setAgoraStreamFallbackOption = async ({
  client,
  channelId,
  uid,
  fallbackType = REMOTE_STREAM_FALLBACK_TYPE.VIDEO_STREAM_LOW,
}) => {
  if (uid == null) return false;

  try {
    await client.setStreamFallbackOption(uid, fallbackType);
    log('setStreamFallbackOption: success', {
      channelId,
      uid,
      fallbackType,
    });
    return true;
  } catch (error) {
    errorLog('setStreamFallbackOption: failed', {
      channelId,
      uid,
      fallbackType,
      error,
    });
    return false;
  }
};

// --- Core Flows ---

const joinChannelWithoutSubscribingCore = async ({
  appId,
  channelId,
  token,
  sessionId,
  agoraInstance,
}) => {
  // load script
  if (!agoraInstance.loadScriptPromise) {
    agoraInstance.loadScriptPromise = loadAgoraScript();
  }
  await waitWithAbort(agoraInstance.loadScriptPromise, agoraInstance);
  if (!getIsCurrentAgoraInstance(agoraInstance)) return null;

  // create client & bind internal listeners
  if (!agoraInstance.createClientPromise) {
    if (!agoraClient) {
      agoraClient = initializeAgoraClient({ codec: 'h264' });
    }
    agoraInstance.client = agoraClient;
    agoraInstance.createClientPromise = Promise.resolve(agoraClient);
    bindInternalClientListeners(agoraClient, agoraInstance, channelId);
  }
  const client = await waitWithAbort(
    agoraInstance.createClientPromise,
    agoraInstance
  );
  if (!getIsCurrentAgoraInstance(agoraInstance)) return null;

  // join channel — channelId is passed as the Agora SDK "channel" parameter
  if (!agoraInstance.joinChannelPromise) {
    if (!appId || !token) {
      throw new Error(
        `joinChannel failed: missing required params (appId: ${!!appId}, token: ${!!token})`
      );
    }
    // setRemoteDefaultVideoStreamType sets the initial subscribed stream type before join.
    await waitWithAbort(
      setAgoraRemoteDefaultVideoStreamType({ client, channelId }),
      agoraInstance
    );
    if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
    log('joinChannelWithoutSubscribing: joining', { channelId });
    agoraInstance.joinChannelPromise = client.join(
      appId,
      channelId,
      token,
      sessionId,
      // Ref: https://doc.shengwang.cn/doc/rtc/javascript/best-practice/preload#22-%E8%BF%9B%E5%85%A5%E9%A2%91%E9%81%93
      // Enable autoSubscribe only in preload flow.
      // We reuse a single active Agora client here, so only current livestream trigger autoSubscribe, thus agora subscribe cost stays limited.
      // In the multi-client (multi agoraClient, pool size > 1) early-join flow, enabling autoSubscribe would subscribe too early
      // and significantly increase Agora cost.
      { autoSubscribe: true }
    );
  }
  try {
    await waitWithAbort(agoraInstance.joinChannelPromise, agoraInstance);
  } catch (error) {
    errorLog('joinChannelWithoutSubscribingCore: join failed', {
      channelId,
      error,
    });
    resetConnection(agoraInstance);
    throw error;
  }
  if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
  log('joinChannelWithoutSubscribing: joined', { channelId });

  // wait for both video & audio published (with timeout)
  if (!agoraInstance.waitForPublishingPromise) {
    const { promise, resolve } = Promise.withResolvers();
    agoraInstance.publishedResolve = resolve;
    agoraInstance.waitForPublishingPromise = Promise.race([
      promise,
      new Promise((_, reject) => {
        agoraInstance.publishingTimeoutId = setTimeout(() => {
          agoraInstance.publishingTimeoutId = null;
          reject(new Error(AGORA_ERROR_WAIT_FOR_PUBLISHING_TIMEOUT));
          // Clear cached rejected promise so the player can retry fresh
          // instead of immediately re-hitting the already-rejected promise.
          resetPublishing(agoraInstance);
        }, WAIT_FOR_PUBLISHING_TIMEOUT_MS);
      }),
    ]);
    if (getHasRequiredPublishedTracks(agoraInstance)) {
      clearTimeout(agoraInstance.publishingTimeoutId);
      agoraInstance.publishingTimeoutId = null;
      resolve();
    }
  }

  return { client };
};

const joinChannelWithSubscribingCore = async ({
  appId,
  channelId,
  token,
  sessionId,
  agoraInstance,
}) => {
  if (!agoraInstance.joinChannelWithoutSubscribingPromise) {
    agoraInstance.joinChannelWithoutSubscribingPromise =
      joinChannelWithoutSubscribingCore({
        appId,
        channelId,
        token,
        sessionId,
        agoraInstance,
      });
  }
  const joinedChannelResult = await waitWithAbort(
    agoraInstance.joinChannelWithoutSubscribingPromise,
    agoraInstance
  );
  if (!joinedChannelResult || !getIsCurrentAgoraInstance(agoraInstance)) {
    return null;
  }
  const { client } = joinedChannelResult;
  log('joinChannelWithSubscribing: joined', { channelId });

  await waitWithAbort(agoraInstance.waitForPublishingPromise, agoraInstance);
  if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
  log('joinChannelWithSubscribing: published', { channelId });

  // subscribe & collect media tracks
  if (!agoraInstance.subscribeTrackPromise) {
    agoraInstance.subscribeTrackPromise = (async () => {
      const existingTracks = collectTracksFromRemoteUsers(
        client,
        agoraInstance
      );
      if (
        hasRequiredTracks({
          hasVideo: existingTracks.videoTrack,
          hasAudio: existingTracks.audioTrack,
          isAudioLive: agoraInstance.isAudioLive,
        })
      ) {
        // setStreamFallbackOption lets Agora automatically downgrade video on
        // weak networks; skip it for audio-live (no video track to fall back).
        if (!agoraInstance.isAudioLive) {
          await waitWithAbort(
            setAgoraStreamFallbackOption({
              client,
              channelId,
              uid: existingTracks.uid,
            }),
            agoraInstance
          );
          if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
        }
        log('joinChannelWithSubscribing: already subscribed', {
          channelId,
          ...existingTracks,
        });
        return existingTracks;
      }
      try {
        log('joinChannelWithSubscribing: subscribing', { channelId });
        for (const [mediaType, remoteUser] of Object.entries(
          agoraInstance.publishedTracks
        )) {
          if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
          await waitWithAbort(
            client.subscribe(remoteUser, mediaType),
            agoraInstance
          );
          if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
          log('joinChannelWithSubscribing: subscribed', {
            channelId,
            mediaType,
          });
          if (mediaType === 'video') {
            await waitWithAbort(
              setAgoraStreamFallbackOption({
                client,
                channelId,
                uid: remoteUser?.uid,
              }),
              agoraInstance
            );
            if (!getIsCurrentAgoraInstance(agoraInstance)) return null;
          }
        }
      } catch (error) {
        errorLog('joinChannelWithSubscribing: subscribe failed', {
          channelId,
          error,
        });
        agoraInstance.subscribeTrackPromise = null;
        throw error;
      }
      return collectTracksFromRemoteUsers(client, agoraInstance);
    })();
  }
  const track = await waitWithAbort(
    agoraInstance.subscribeTrackPromise,
    agoraInstance
  );
  if (!track || !getIsCurrentAgoraInstance(agoraInstance)) return null;
  return { client, track };
};

// --- Exported API ---

export const joinChannelWithoutSubscribing = async ({
  appId,
  channelId,
  token,
  sessionId,
  isAudioLive = false,
  sourceId,
}) => {
  const agoraInstance = await acquireAgoraInstance({
    channelId,
    isAudioLive,
    sourceId,
  });
  if (!agoraInstance.joinChannelWithoutSubscribingPromise) {
    agoraInstance.joinChannelWithoutSubscribingPromise =
      joinChannelWithoutSubscribingCore({
        appId,
        channelId,
        token,
        sessionId,
        agoraInstance,
      });
  }
  try {
    const joinedChannelResult = await waitWithAbort(
      agoraInstance.joinChannelWithoutSubscribingPromise,
      agoraInstance
    );
    if (!joinedChannelResult) return null;
    const { client } = joinedChannelResult;
    return client;
  } catch (error) {
    errorLog('joinChannelWithoutSubscribing: failed', { channelId, error });
    resetPublishing(agoraInstance);
    leaveAgoraChannel({ channelId });
    throw error;
  }
};

export const joinChannelWithSubscribing = async ({
  appId,
  channelId,
  token,
  sessionId,
  isAudioLive = false,
  sourceId,
}) => {
  const agoraInstance = await acquireAgoraInstance({
    channelId,
    isAudioLive,
    sourceId,
  });
  if (!agoraInstance.joinChannelWithSubscribingPromise) {
    agoraInstance.joinChannelWithSubscribingPromise =
      joinChannelWithSubscribingCore({
        appId,
        channelId,
        token,
        sessionId,
        agoraInstance,
      });
  }
  try {
    const subscribedChannelResult = await waitWithAbort(
      agoraInstance.joinChannelWithSubscribingPromise,
      agoraInstance
    );
    if (!subscribedChannelResult) return null;
    const { client, track } = subscribedChannelResult;
    return { client, track };
  } catch (error) {
    errorLog('joinChannelWithSubscribing: failed', { channelId, error });
    resetConnection(agoraInstance);
    leaveAgoraChannel({ channelId });
    throw error;
  }
};

// https://doc.shengwang.cn/api-ref/rtc/javascript/interfaces/iagorartcclient#setRemoteVideoStreamType
export const setAgoraRemoteVideoStreamType = async ({
  channelId,
  uid,
  streamType,
  shouldDisableFallback = false,
}) => {
  const client = getAgoraInstance({ channelId })?.client;

  if (!client) {
    errorLog('setRemoteVideoStreamType: agora client not found', {
      channelId,
      uid,
      streamType,
      shouldDisableFallback,
    });
    return false;
  }

  try {
    if (shouldDisableFallback) {
      await setAgoraStreamFallbackOption({
        client,
        channelId,
        uid,
        fallbackType: REMOTE_STREAM_FALLBACK_TYPE.DISABLE,
      });
    }

    await client.setRemoteVideoStreamType(uid, streamType);
    log('setRemoteVideoStreamType: success', {
      channelId,
      uid,
      streamType,
      shouldDisableFallback,
    });
    return true;
  } catch (error) {
    errorLog('setRemoteVideoStreamType: failed', {
      channelId,
      uid,
      streamType,
      shouldDisableFallback,
      error,
    });
    throw error;
  }
};

export const setAgoraRemoteStreamFallbackOption = async ({
  channelId,
  uid,
  fallbackType,
}) => {
  const client = getAgoraInstance({ channelId })?.client;

  if (!client) {
    errorLog('setStreamFallbackOption: agora client not found', {
      channelId,
      uid,
      fallbackType,
    });
    return false;
  }

  return setAgoraStreamFallbackOption({
    client,
    channelId,
    uid,
    fallbackType,
  });
};

export const cleanupAllAgoraInstances = async () => {
  if (!currentAgoraInstance) return;
  await leaveAgoraChannel({ channelId: currentAgoraInstance.channelId });
};

export const unsubscribeJoinedChannel = async ({ channelId }) => {
  const agoraInstance = getAgoraInstance({ channelId });
  if (!agoraInstance || !agoraInstance.client) {
    errorLog('unsubscribeJoinedChannel: agora client not found', { channelId });
    return;
  }
  log('unsubscribeJoinedChannel: start', { channelId });
  resetSubscription(agoraInstance);
  const remoteUsers = agoraInstance.client?.remoteUsers;
  if (!remoteUsers?.length) {
    errorLog('unsubscribeJoinedChannel: no remote users', { channelId });
    return;
  }
  try {
    for (const remoteUser of remoteUsers) {
      if (remoteUser?.videoTrack) {
        await agoraInstance.client.unsubscribe(remoteUser, 'video');
      }
      if (remoteUser?.audioTrack) {
        await agoraInstance.client.unsubscribe(remoteUser, 'audio');
      }
    }
  } catch (error) {
    errorLog('unsubscribeJoinedChannel failed', { channelId, error });
  }
  log('unsubscribeJoinedChannel: done', { channelId });
};

export const preloadAgoraChannel = async ({
  appId,
  channel,
  token,
  sessionId,
}) => {
  log('preload channel start', { appId, channel, token, sessionId });
  try {
    await loadAgoraScript();
    await window?.AgoraRTC?.preload(appId, channel, token, sessionId);
    log('successfully preloaded channel', {
      appId,
      channel,
      token,
      sessionId,
    });
  } catch (error) {
    errorLog('preload channel failed', {
      error,
      appId,
      channel,
      token,
      sessionId,
    });
  }
};
